/**
 * Formats a Date to: "Jan 5, 2025, 3:45 PM"
 * Used for: Table cells (dateLastEdited, dateLastResponse)
 */
export const formatDateShort = (timestamp: number | null): string => {
  if (!timestamp) return '';

  const date = new Date(timestamp * 1000);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Formats seconds into a "M:SS" countdown string.
 * Used for: IdleWarningDialog countdown display
 *
 * @example formatTimeMMSS(125) // "2:05"
 * @example formatTimeMMSS(60)  // "1:00"
 * @example formatTimeMMSS(9)   // "0:09"
 */
export function formatTimeMMSS(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Converts a Julian Date Number (JDN) to a Unix timestamp in seconds.
 * Used for: Formatting dataset lastEdited dates from the API (stored as JDN)
 *
 * @example julianToUnix(2440588) // 43200  (Jan 2, 1970, 12:00 UTC)
 * @example julianToUnix(2460677) // ~1735689600  (Jan 1, 2025)
 */
export const julianToUnix = (jdn: number): number => (jdn - 2440587.5) * 86400;

/**
 * Parses a RFC 4180-style CSV string into an array of trimmed field values.
 * Fields containing commas must be double-quoted; escaped quotes use "".
 *
 * @example parseSources('Alpha,Beta,"Survey, Third"') // ['Alpha', 'Beta', 'Survey, Third']
 * @example parseSources('"A ""quoted"" name",B')     // ['A "quoted" name', 'B']
 */
export function parseSources(csv: string): string[] {
  if (!csv) return [];
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < csv.length; i++) {
    const char = csv[i];
    if (char === '"') {
      if (inQuotes && csv[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  if (current.trim()) result.push(current.trim());
  return result;
}
